import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import type { Locale, Translations } from "./types";
import { en } from "./en";

const translations: Record<Locale, Translations> = {
  en,
  vi: en, // placeholder — replaced in language files
  zh: en,
  ja: en,
  id: en,
  th: en,
  es: en,
};

export function registerTranslation(locale: Locale, t: Translations) {
  translations[locale] = t;
}

function detectLocale(): Locale {
  const stored = localStorage.getItem("kra-guide-lang");
  if (stored && stored in translations) return stored as Locale;

  const browserLang = navigator.language.slice(0, 2).toLowerCase();
  const map: Record<string, Locale> = {
    en: "en",
    vi: "vi",
    zh: "zh",
    ja: "ja",
    id: "id",
    th: "th",
    es: "es",
  };
  return map[browserLang] ?? "en";
}

interface I18nContextValue {
  locale: Locale;
  t: Translations;
  setLocale: (l: Locale) => void;
}

export const I18nContext = createContext<I18nContextValue>({
  locale: "en",
  t: en,
  setLocale: () => {},
});

export function useI18n() {
  return useContext(I18nContext);
}

export function useI18nState(): I18nContextValue {
  const [locale, setLocaleState] = useState<Locale>(detectLocale);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    localStorage.setItem("kra-guide-lang", l);
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  return { locale, t: translations[locale], setLocale };
}

export type { Locale, Translations };
export { LOCALE_CONFIG } from "./types";
